import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Linkedin,
  Mail,
  Quote,
  Brain,
  Compass,
  ShieldCheck,
} from 'lucide-react';

const ADAM_PHOTO =
  'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/2x9ios9b_image.png';
const BROTHERHOOD_EMBLEM =
  'https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/8w3x6659_brotherhood-reset-logo.jpg';

const FOCUS = [
  {
    icon: Brain,
    title: 'AI Strategy',
    body: 'Operating models that turn executive AI ambitions into programs that actually ship.',
  },
  {
    icon: ShieldCheck,
    title: 'AI Governance',
    body: 'Controls, accountability, and ownership structures brothers can trust at scale.',
  },
  {
    icon: Compass,
    title: 'Program Clarity',
    body: 'Clear owners, honest timelines, and just enough chaos to surface the gaps that matter.',
  },
];

export default function SpeakerAdamHarte() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Soft background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#D4AF37]/8 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
      </div>

      <div className="relative max-w-2xl mx-auto px-5 py-10" data-testid="speaker-adam-page">
        {/* Back link */}
        <Link
          to="/portal/monthly-meeting"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#D4AF37] font-manrope text-sm transition-colors mb-6"
          data-testid="speaker-adam-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the Circle
        </Link>

        {/* Card */}
        <article className="rounded-3xl bg-gradient-to-br from-[#111111] via-[#111111] to-[#0f0a05] border border-[#D4AF37]/25 overflow-hidden shadow-2xl shadow-black/60">
          {/* Hero */}
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={ADAM_PHOTO}
                alt="Adam Harte"
                className="w-full h-full object-cover object-top grayscale-[0.15]"
                data-testid="speaker-adam-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/55 to-transparent pointer-events-none" />
            </div>

            {/* Chip + emblem */}
            <div className="absolute top-5 left-5 bg-[#0A0A0A]/85 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1.5 rounded-full">
              Guest Speaker
            </div>
            <img
              src={BROTHERHOOD_EMBLEM}
              alt=""
              aria-hidden="true"
              className="absolute top-5 right-5 w-12 h-12 rounded-full object-cover border border-[#D4AF37]/40"
            />

            {/* Name block over gradient */}
            <div className="absolute left-0 right-0 bottom-0 px-7 pb-6">
              <h1 className="font-cormorant text-4xl md:text-5xl font-medium leading-tight mb-1">
                Adam Harte
              </h1>
              <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.3em] font-bold">
                Enterprise AI Strategy &amp; Governance Advisor
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="px-7 py-7 space-y-7">
            {/* Talk */}
            <div
              className="rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/8 p-5"
              data-testid="speaker-adam-talk"
            >
              <p className="text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.3em] font-bold mb-2">
                The talk
              </p>
              <p className="font-cormorant text-2xl md:text-3xl text-white leading-snug">
                Who Controls the Future?
              </p>
              <p className="text-zinc-400 font-manrope text-sm italic mt-1">
                Black Men &amp; the Rise of AI.
              </p>
            </div>

            {/* Bio */}
            <div className="space-y-4 text-zinc-300 font-manrope text-[15px] leading-relaxed">
              <p>
                Adam Harte is an Enterprise AI Strategy and AI Governance advisor
                who partners with executives to design the operating models,
                controls, and accountability structures that make large-scale AI
                programs actually work.
              </p>
              <p>
                A recovering program manager and new father, he brings an
                unapologetically opinionated view to his craft:{' '}
                <span className="text-white">
                  every good system starts with a clear owner
                </span>{' '}
                &mdash; while leaving just enough room for the chaos that
                surfaces the gaps worth fixing.
              </p>
              <p>
                He joins the Circle this fall to lead a conversation Black men
                can&rsquo;t afford to miss &mdash; how AI is reshaping power,
                who holds it, and the moves our community needs to make right
                now.
              </p>
            </div>

            {/* Pull quote */}
            <figure
              className="relative rounded-2xl bg-[#0A0A0A]/70 border-l-2 border-[#D4AF37] px-5 py-5"
              data-testid="speaker-adam-quote"
            >
              <Quote className="absolute -top-2 left-3 w-4 h-4 text-[#D4AF37] bg-[#111111] px-0.5" />
              <blockquote className="font-cormorant text-xl md:text-2xl italic text-white leading-snug">
                &ldquo;Every good system starts with a clear owner &mdash; and
                just enough chaos to find the holes.&rdquo;
              </blockquote>
            </figure>

            {/* Focus areas */}
            <div className="grid sm:grid-cols-3 gap-3" data-testid="speaker-adam-focus">
              {FOCUS.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    className="rounded-xl bg-[#0A0A0A]/60 border border-white/5 p-4"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center mb-2">
                      <Icon className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <p className="font-cormorant text-lg text-white mb-1">
                      {f.title}
                    </p>
                    <p className="text-zinc-400 font-manrope text-xs leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                to="/portal/monthly-meeting"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-manrope text-sm font-semibold hover:bg-amber-200 transition-colors"
                data-testid="speaker-adam-join-cta"
              >
                Join the next Circle <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:nalvanzo@gmail.com?subject=Question%20for%20Adam%20Harte%20%E2%80%94%20Circle"
                className="inline-flex items-center justify-center gap-2 border border-white/15 text-white px-6 py-3 rounded-full font-manrope text-sm font-medium hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
                data-testid="speaker-adam-question-cta"
              >
                <Mail className="w-4 h-4" /> Send a question
              </a>
            </div>

            {/* Footer brand */}
            <div className="pt-5 border-t border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src={BROTHERHOOD_EMBLEM}
                  alt="The Brotherhood Circle"
                  className="w-7 h-7 rounded-full object-cover border border-[#D4AF37]/40"
                />
                <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest">
                  The Reset &middot; The Brotherhood Circle
                </p>
              </div>
              <Link
                to="/portal"
                className="text-zinc-500 hover:text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest transition-colors"
              >
                Visit the portal
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
